/**
 * The Decisions surface's data plane, in one injectable object.
 *
 * Every read and write the surface makes goes through `DecisionsApi`, so a
 * test can hand the shell a stub and the components never know a network
 * exists. The default talks to `/api/yagni-code-decisions` (session auth).
 *
 * Errors carry the server's message when it sent one: the surface shows the
 * reason a change did not save, never a bare status code.
 */
import { createContext, useContext } from "react";
import type {
  CodeDecision,
  CodeDecisionRelatedResponse,
  CodeDecisionScopes,
  CodeDecisionsResponse,
  CodeDecisionsSummary,
  RecordCodeDecisionRequest,
  RecordCodeDecisionResponse,
} from "@yagni/shared";

const BASE = "/api/yagni-code-decisions";

/** The surface's named bands. `all` is the plain list. */
export type DecisionsView = "all" | "needs_review" | "confirmed" | "history";

export interface DecisionsQuery {
  q?: string;
  view?: DecisionsView;
  /** A Team id: the decisions on the repositories that Team attaches. */
  team?: string;
  repo?: string;
  /** Only workspace-wide rows (recorded with no repository). */
  scope?: "workspace";
  workItem?: string;
  /** Only rows a given person recorded (`user_id`); the "recorded by me" filter. */
  author?: string;
  sort?: "recent" | "cited";
  limit?: number;
  offset?: number;
}

export type SupersedeBody = {
  successorId?: string;
  question?: string;
  decision?: string;
  rationale?: string;
};

export type RecordResult =
  | { kind: "recorded"; id: string }
  | { kind: "duplicate"; existing: CodeDecision };

/** The scope axes the summary shares with the list, so counts match rows. */
export interface SummaryScope {
  team?: string;
  repo?: string;
  workspaceOnly?: boolean;
}

export function buildSummarySearch(scope: SummaryScope): URLSearchParams {
  const search = new URLSearchParams();
  if (scope.team) search.set("team", scope.team);
  if (scope.repo) search.set("repo", scope.repo);
  if (scope.workspaceOnly) search.set("scope", "workspace");
  return search;
}

export interface DecisionsApi {
  list(query: DecisionsQuery): Promise<CodeDecisionsResponse>;
  /** One decision by id; null when it is not in this workspace. */
  get(id: string): Promise<CodeDecision | null>;
  summary(scope?: SummaryScope): Promise<CodeDecisionsSummary>;
  scopes(): Promise<CodeDecisionScopes>;
  related(id: string): Promise<CodeDecisionRelatedResponse>;
  verify(id: string, verified: boolean): Promise<void>;
  supersede(id: string, body: SupersedeBody): Promise<void>;
  discard(id: string): Promise<void>;
  reactivate(id: string): Promise<void>;
  record(body: RecordCodeDecisionRequest): Promise<RecordResult>;
}

export class DecisionsApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "DecisionsApiError";
    this.status = status;
  }
}

async function readError(res: Response, fallback: string): Promise<DecisionsApiError> {
  let message = `${fallback} (${res.status})`;
  try {
    const data = (await res.json()) as { error?: unknown };
    if (typeof data.error === "string" && data.error) message = data.error;
  } catch {
    // Non-JSON error body: keep the status-code message.
  }
  return new DecisionsApiError(message, res.status);
}

async function getJson<T>(path: string, fallback: string): Promise<T> {
  const res = await fetch(path, { credentials: "include" });
  if (!res.ok) throw await readError(res, fallback);
  return (await res.json()) as T;
}

async function postJson<T>(path: string, body: unknown, fallback: string): Promise<T> {
  const res = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) throw await readError(res, fallback);
  return (await res.json()) as T;
}

export function buildListSearch(query: DecisionsQuery): URLSearchParams {
  const search = new URLSearchParams();
  if (query.q) search.set("q", query.q);
  if (query.view && query.view !== "all") search.set("view", query.view);
  if (query.team) search.set("team", query.team);
  if (query.repo) search.set("repo", query.repo);
  if (query.scope) search.set("scope", query.scope);
  if (query.workItem) search.set("workItem", query.workItem);
  if (query.author) search.set("author", query.author);
  if (query.sort) search.set("sort", query.sort);
  if (typeof query.limit === "number") search.set("limit", String(query.limit));
  if (typeof query.offset === "number" && query.offset > 0) {
    search.set("offset", String(query.offset));
  }
  return search;
}

export const decisionsApi: DecisionsApi = {
  list: (query) =>
    getJson<CodeDecisionsResponse>(
      `${BASE}?${buildListSearch(query).toString()}`,
      "Failed to load decisions",
    ),
  get: async (id) => {
    const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, { credentials: "include" });
    if (res.status === 404) return null;
    if (!res.ok) throw await readError(res, "Failed to load this decision");
    const data = (await res.json()) as { decision?: CodeDecision };
    // A 200 without a decision is a broken contract, not a missing record.
    if (!data.decision) throw new DecisionsApiError("Malformed response from the ledger", 200);
    return data.decision;
  },
  summary: async (scope = {}) => {
    const qs = buildSummarySearch(scope).toString();
    const data = await getJson<Partial<CodeDecisionsSummary>>(
      qs ? `${BASE}/summary?${qs}` : `${BASE}/summary`,
      "Failed to load the summary",
    );
    // Counts or nothing: a body that is not the summary (a permissive stub, a
    // proxy's HTML) must not become a row that says "0" or a strip that
    // renders on `undefined`.
    if (typeof data.active !== "number" || typeof data.leanedOn !== "number") {
      throw new DecisionsApiError("Malformed summary from the ledger", 200);
    }
    return {
      active: data.active,
      confirmed: data.confirmed ?? 0,
      asserted: data.asserted ?? 0,
      leanedOn: data.leanedOn,
      retired: data.retired ?? 0,
      recent: data.recent ?? 0,
      citations: data.citations ?? 0,
      repos: data.repos ?? 0,
      workspaceWide: data.workspaceWide ?? 0,
    };
  },
  scopes: async () => {
    const data = await getJson<Partial<CodeDecisionScopes>>(`${BASE}/scopes`, "Failed to load scopes");
    // The filters are an affordance, not the content: a malformed body costs
    // the dropdowns, never the ledger.
    return {
      repos: Array.isArray(data.repos) ? data.repos : [],
      teams: Array.isArray(data.teams) ? data.teams : [],
    };
  },
  related: (id) =>
    getJson<CodeDecisionRelatedResponse>(
      `${BASE}/${encodeURIComponent(id)}/related`,
      "Failed to load related decisions",
    ),
  verify: async (id, verified) => {
    await postJson(`${BASE}/${encodeURIComponent(id)}/verify`, { verified }, "The change did not save");
  },
  supersede: async (id, body) => {
    await postJson(`${BASE}/${encodeURIComponent(id)}/supersede`, body, "The change did not save");
  },
  discard: async (id) => {
    await postJson(`${BASE}/${encodeURIComponent(id)}/discard`, {}, "The change did not save");
  },
  reactivate: async (id) => {
    await postJson(`${BASE}/${encodeURIComponent(id)}/reactivate`, {}, "The change did not save");
  },
  record: async (body) => {
    const res = await fetch(BASE, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    if (res.status === 409) {
      const data = (await res.json()) as { existing?: CodeDecision };
      if (data.existing) return { kind: "duplicate", existing: data.existing };
    }
    if (!res.ok) throw await readError(res, "The decision did not save");
    const data = (await res.json()) as RecordCodeDecisionResponse;
    return { kind: "recorded", id: data.id };
  },
};

const DecisionsApiContext = createContext<DecisionsApi>(decisionsApi);

export const DecisionsApiProvider = DecisionsApiContext.Provider;

export function useDecisionsApi(): DecisionsApi {
  return useContext(DecisionsApiContext);
}
